import React from 'react';
import { motion } from 'framer-motion';
import { OpenSnowsightButton } from './OpenSnowsightButton';

interface NextStepsListProps {
  accountIdentifier: string;
}

/**
 * NextStepsList Component
 *
 * Displays numbered steps for completing Snowflake setup.
 *
 * @component
 * @example
 * ```tsx
 * <NextStepsList accountIdentifier="myorg-myaccount" />
 * ```
 */
export const NextStepsList: React.FC<NextStepsListProps> = ({ accountIdentifier }) => {
  return (
    <motion.div
      className="space-y-3"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.3 }}
    >
      <h4 className="text-sm text-dataops-dark mb-3">Next Steps:</h4>
      <div className="grid gap-3">
        <div className="flex gap-4 items-start bg-white rounded-lg p-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-dataops-primary-blue/10 flex items-center justify-center">
            <span className="text-lg text-dataops-primary-blue">1</span>
          </div>
          <p className="text-sm text-primary-text pt-1">Copy the SQL commands below</p>
        </div>

        <div className="flex gap-4 items-start bg-white rounded-lg p-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-dataops-primary-blue/10 flex items-center justify-center">
            <span className="text-lg text-dataops-primary-blue">2</span>
          </div>
          <div className="flex items-center gap-3 pt-0.5">
            <p className="text-sm text-primary-text">Open Snowsight</p>
            <OpenSnowsightButton accountIdentifier={accountIdentifier} />
          </div>
        </div>

        <div className="flex gap-4 items-start bg-white rounded-lg p-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-dataops-primary-blue/10 flex items-center justify-center">
            <span className="text-lg text-dataops-primary-blue">3</span>
          </div>
          <p className="text-sm text-primary-text pt-1">Run the SQL commands in Snowflake Worksheet</p>
        </div>

        <div className="flex gap-4 items-start bg-white rounded-lg p-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-dataops-primary-blue/10 flex items-center justify-center">
            <span className="text-lg text-dataops-primary-blue">4</span>
          </div>
          <p className="text-sm text-primary-text pt-1">Check the confirmation box</p>
        </div>

        <div className="flex gap-4 items-start bg-white rounded-lg p-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-dataops-primary-blue/10 flex items-center justify-center">
            <span className="text-lg text-dataops-primary-blue">5</span>
          </div>
          <p className="text-sm text-primary-text pt-1">Test the connection</p>
        </div>
      </div>
    </motion.div>
  );
};
